import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  InputAdornment,
  Menu,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import InventoryIcon from '@mui/icons-material/Inventory';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { fetchSearchPage, handleError, formatCurrency } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const ProductsReport = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setNavigation, state } = useNavigationHelperStore();
  
  // Load saved search parameters when returning to the page
  useEffect(() => {
    if (state) {
      const { page: savedPage, data: savedData, searchTerm: savedSearchTerm } = state;
      if (savedPage) setPage(savedPage);
      if (savedData) setData(savedData);
      if (savedSearchTerm) setSearchTerm(savedSearchTerm);
      
      if (savedPage && savedSearchTerm) {
        fetchProducts(savedPage, savedSearchTerm);
      } else {
        fetchProducts(1);
      }
    }
  }, []);

  // Save search parameters when leaving this page
  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      page,
      data,
      searchTerm
    });
  };

  const fetchProducts = async (pageNumber, search = '') => {
    setIsLoading(true);
    try {      
      const response = await fetchSearchPage('products', search, pageNumber);
      setData(response);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchProducts(value, searchTerm);
  };

  const handleSearch = () => {
    setPage(1);
    fetchProducts(1, searchTerm);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleProductClick = (product) => {
    // Save current state before navigating
    saveCurrentState();
    navigate('/main/view-product', { state: { id: product.id } });
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleExport = async (type) => {
    handleMenuClose();
    setIsExporting(true);
    
    const config = {
      PRODUCTS_EXCEL: { 
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 
        filename: 'productos.xlsx',
        message: t('reports.productsExported')
      },
      SERVICES_BARCODES: { 
        mimeType: 'application/pdf', 
        filename: 'codigos_barras_servicios.pdf',
        message: t('reports.barcodesExported')
      },
      PRODUCTS_PRICES: { 
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 
        filename: 'productos_precios.xlsx',
        message: t('reports.pricesExported')
      }
    };

    try {
      const params = { type, page: 1 };
      
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}reports`, 
        { params, withCredentials: true }
      );
      
      if (response.data && response.data.status === "SUCCESS" && response.data.data) {
        const byteCharacters = atob(response.data.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: config[type].mimeType });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', config[type].filename);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        
        snackbar(config[type].message);
      } else {
        throw new Error(t('reports.exportInvalidFormat'));
      }
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsExporting(false);
    }
  };

  const getStockStatusChip = (product) => {
    if (product.is_service) {
      return <Chip label={t('reports.serviceChip')} color="info" size="small" />;
    }

    if (product.quantity <= 0) {
      return <Chip label={t('reports.noStock')} color="error" size="small" />;
    }
    
    if (product.quantity > 0 && product.quantity < 5) {
      return <Chip label={t('reports.lowStock')} color="warning" size="small" />;
    }
    
    return <Chip label={t('reports.inStock')} color="success" size="small" />;
  };

  const getProductCategories = (categories) => {
    if (!categories || categories.length === 0) return t('reports.noCategory');
    return categories.map(cat => cat.name).join(', ');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder={t('reports.searchProducts')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleKeyPress}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon 
                  sx={{ cursor: 'pointer' }} 
                  onClick={handleSearch}
                />
              </InputAdornment>
            ),
          }}
        />
        <CloudDownloadIcon 
          onClick={handleMenuOpen}
          sx={{ 
            fontSize: 28, 
            cursor: 'pointer',
            color: isExporting ? 'text.disabled' : 'text.primary'
          }}
        />
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleExport('PRODUCTS_EXCEL')}>{t('reports.productsTitle')}</MenuItem>
          <MenuItem onClick={() => handleExport('PRODUCTS_PRICES')}>{t('reports.productsPrices')}</MenuItem>
          <MenuItem onClick={() => handleExport('SERVICES_BARCODES')}>{t('reports.barcodesForServices')}</MenuItem>
        </Menu>
      </Box>
      
      {data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2}>
                <Typography variant="h6">
                  {t('reports.productsTitle')} ({data.total_results || data.content.length})
                </Typography>
              </Box>
              
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('reports.productLabel')}</TableCell>
                      <TableCell>{t('common.category')}</TableCell>
                      <TableCell>{t('reports.stockLabel')}</TableCell>
                      <TableCell align="right">{t('reports.pricesLabel')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.content.map((product) => (
                      <TableRow 
                        key={product.id}
                        hover
                        onClick={() => handleProductClick(product)}
                        sx={{ '&:hover': { cursor: 'pointer' } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {product.thumb_url ? (
                              <Avatar
                                src={product.thumb_url}
                                variant="rounded"
                                sx={{ width: 40, height: 40 }}
                              />
                            ) : (
                              <Avatar
                                variant="rounded"
                                sx={{ 
                                  width: 40, 
                                  height: 40, 
                                  bgcolor: product.is_service ? 'secondary.main' : 'primary.main' 
                                }}
                              >
                                {product.is_service ? <MedicalServicesIcon /> : <InventoryIcon />}
                              </Avatar>
                            )}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {product.name}
                              </Typography>
                              {product.bar_code && (
                                <Typography variant="caption" color="text.secondary">
                                  {product.bar_code}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getProductCategories(product.categories)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {product.is_service ? (
                            <Typography variant="body2" color="text.secondary">
                              {t('reports.na')}
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              {product.quantity_string || '0'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {product.price_es || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noProducts')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.noProductsToShow')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProductsReport; 